import { globalKeys } from 'assets/data/global-keys';
import { useAuthStore } from '~/stores/auth.store';
import { useSiteStore } from '~/stores/site.store';
import { uuid } from '~/utilities/uuid';
import type {
  EOccurance,
  IExternalToast,
  IViewedToast,
} from '~/interfaces/content/marketing-toasts';

export default defineNuxtPlugin({
  name: 'toasts',
  dependsOn: ['init-client', 'directives'],
  parallel: true,
  async setup() {
    const { $storageService } = useServices();

    const auth = useAuthStore();
    const site = useSiteStore();

    const toastsAllowedPerSession =
      site.config?.devConfig?.toastPerSessionThreshold || 1;

    const toasts = computed<Map<string, IExternalToast>>(() => {
      const rawToasts =
        $storageService.getLocalStorage<IExternalToast[]>({
          key: globalKeys.marketingKeys.toasts,
        }) || [];

      const filtered = rawToasts.filter((toast) => {
        return auth.isLoggedIn
          ? toast.state === 'LoggedIn' || toast.state === 'Both'
          : toast.state === 'LoggedOut' || toast.state === 'Both';
      });

      return new Map(filtered.map((t) => [t.id, t]));
    });

    const toastsArray = computed<IExternalToast[]>(() =>
      Array.from(toasts.value.values()),
    );

    const viewedToasts = computed<Map<string, IViewedToast>>(() => {
      const raw =
        $storageService.getLocalStorage<IViewedToast[]>({
          key: globalKeys.marketingKeys.viewedToasts,
        }) || [];
      return new Map(raw.map((t) => [t.id, t]));
    });

    const nextToast = computed<IExternalToast>(() => {
      const allToasts = toastsArray.value;

      if (viewedToasts.value.size === 0 && allToasts.length > 0) {
        return allToasts[0];
      }

      const thresholdMet = $storageService.getSessionStorage({
        key: globalKeys.marketingKeys.toastsPerSessionThresholdMet,
      });

      if (thresholdMet) return undefined;

      return allToasts.find((t) => !viewedToasts.value.has(t.id));
    });

    const calculateNextViewDate = (occurrence: EOccurance) => {
      const date = new Date();
      if (occurrence === 'Daily')
        return new Date(date.setDate(date.getDate() + 1));
      if (occurrence === 'Weekly')
        return new Date(date.setDate(date.getDate() + 7));
      return false;
    };

    const uniquenessGenerator = (name: string, path: string): string => {
      return `${btoa(`${name.replace(/\s/g, '-')}-${path.replace(/\s/g, '-')}`)}-${uuid()}`;
    };

    const addToast = (data: IExternalToast | IExternalToast[]) => {
      const newToasts = Array.isArray(data) ? data : [data];
      $storageService.setLocalStorage({
        key: globalKeys.marketingKeys.toasts,
        data: [...newToasts, ...toastsArray.value],
      });
    };

    const clearViewedToasts = () => {
      $storageService.removeLocalStorage({
        key: globalKeys.marketingKeys.viewedToasts,
      });
    };

    const generateToast = (contentPath: string) => {
      const toast: IViewedToast = {
        name: 'Test toast',
        includedAccountTags: '',
        excludedAccountTags: '',
        startDateTime: new Date().toISOString(),
        endDateTime: new Date().toISOString(),
        contentPath,
        state: 'Both',
        occurance: 'OnceOff',
        id: uniquenessGenerator('Test toast', contentPath),
        viewed: new Date().toISOString(),
        nextShowDate: false,
      };

      $storageService.setSessionStorage({
        key: globalKeys.marketingKeys.toastsPerSessionThresholdMet,
        data: false,
      });

      $storageService.setLocalStorage({
        key: globalKeys.marketingKeys.toasts,
        data: [toast, ...Array.from(toasts.value.values())],
      });
    };

    const setToastViewed = (toast: IExternalToast) => {
      if (!toast) return;

      const alreadyViewed = viewedToasts.value.has(toast.id);

      if (!alreadyViewed) {
        const updatedViewed = [
          ...Array.from(viewedToasts.value.values()),
          {
            ...toast,
            viewed: new Date().toISOString(),
            nextShowDate: calculateNextViewDate(toast.occurance),
          },
        ];

        $storageService.setLocalStorage({
          key: globalKeys.marketingKeys.viewedToasts,
          data: updatedViewed,
        });
      }

      const currentSessionCount = Number(
        $storageService.getSessionStorage({
          key: globalKeys.marketingKeys.toastsPerSession,
        }) || 0,
      );

      const newCount = currentSessionCount + 1;

      $storageService.setSessionStorage({
        key: globalKeys.marketingKeys.toastsPerSession,
        data: newCount,
      });

      if (newCount >= toastsAllowedPerSession) {
        $storageService.setSessionStorage({
          key: globalKeys.marketingKeys.toastsPerSessionThresholdMet,
          data: true,
        });
      }
    };

    const toastInterface = {
      toasts,
      nextToast,
      viewedToasts,
      toastsAllowedPerSession,
      viewedToastThresholdMet: computed(
        () => viewedToasts?.value?.size >= toastsAllowedPerSession,
      ),
      addToast,
      clearViewedToasts,
      generateToast,
      setToastViewed,
    };

    return {
      provide: {
        toastService: toastInterface,
      },
    };
  },
});
